<template>
  <!-- Dashboard de rendimientos (project.configs.dashboards → suffix_route: 'performance') -->
  <div class="-mx-4 -mt-4">
    <page-header :title="$t('performance_dashboard_weekly_summary_title')" :subtitle="headerSubtitle">
      <!-- Exportar a PDF: el backend lo genera (DomPDF) y devuelve una URL temporal de descarga -->
      <div class="flex justify-end w-full">
        <button type="button" :title="$t('export')" :disabled="exportingPdf || !projectWeek"
          :class="['inline-flex items-center gap-2 px-3 py-1 text-sm font-semibold text-white rounded bg-red-dark focus:outline-none',
                   exportingPdf ? 'opacity-75 cursor-wait' : 'hover:bg-red-darker']"
          @click="exportPdf">
          <i :class="exportingPdf ? 'fas fa-spinner fa-spin' : 'fas fa-file-pdf'"></i>
          {{ $t(exportingPdf ? 'generating' : 'export') }}
        </button>
      </div>
    </page-header>

    <div class="flex flex-col gap-3 p-4 text-xs text-grey-darkest">

      <!-- Qué se analiza y con qué criterio -->
      <div>
        <p class="font-semibold">
          {{ analyzedSummary }}
          <span class="font-normal text-grey-darker">({{ $t('performance_dashboard_why') }})</span>
          <info-button topic="summary" @open="openInfo" />
        </p>
        <p class="mt-1 italic text-grey-darker">
          {{ $t('performance_dashboard_criterion_label') }}
          {{ $t(weightByHours ? 'performance_dashboard_selection_criteria_takeoff' : 'performance_dashboard_selection_criteria', { weight: performanceThresholds.weight, deviation: performanceThresholds.deviation }) }}
          <info-button topic="selection" @open="openInfo" />
        </p>
      </div>

      <!-- Indicadores: P1/P2, retraso del proyecto y acciones vencidas, con su semáforo -->
      <div class="grid grid-cols-1 md:grid-cols-3 border border-grey-light rounded-lg overflow-hidden">
        <div v-for="(kpi, i) in kpis" :key="kpi.label"
          :class="['flex flex-col bg-grey-lighter', { 'border-t md:border-t-0 md:border-l border-grey-light': i > 0 }]">
          <div class="flex-1 px-4 py-3">
            <div class="font-bold uppercase tracking-wide text-grey-darker">
              {{ kpi.label }}
              <info-button :topic="kpi.info" @open="openInfo" />
            </div>
            <div class="mt-1 text-2xl font-bold text-blue-darkest leading-tight">{{ kpi.value }}</div>
          </div>
          <div :class="['px-4 py-1 text-center font-semibold', FOOTER_VARIANTS[kpi.variant]]">● {{ kpi.footer }}</div>
        </div>
      </div>

      <!-- 1. A pasado y a futuro, por actividad -->
      <h3 class="mt-1 text-sm font-bold text-blue-darkest">{{ $t('performance_dashboard_section_past_future') }}</h3>

      <div class="grid grid-cols-1 xl:grid-cols-3 gap-3">
        <div class="flex flex-col px-4 py-3 bg-white border border-grey-light rounded-lg">
          <h4 class="text-sm font-bold text-center text-blue-darkest">
            {{ $t('performance_dashboard_progress_to_date') }}
            <info-button topic="chart_progress" @open="openInfo" />
          </h4>
          <p class="mb-2 text-center text-grey-darker">{{ $t('performance_dashboard_progress_to_date_subtitle') }}</p>
          <column-chart
            :data="progressChartData"
            :colors="[COLORS.budgeted, COLORS.planned, COLORS.actual]"
            :min="0" :max="100" suffix="%"
            :library="chartLibrary"
            :messages="{ empty: loadingPerformance ? $t('loading') : $t('performance_dashboard_no_chart_data') }"
            height="300px" />
          <p class="mt-2 text-grey-darker">{{ $t('performance_dashboard_progress_to_date_note') }}</p>
        </div>

        <div class="flex flex-col px-4 py-3 bg-white border border-grey-light rounded-lg">
          <h4 class="text-sm font-bold text-center text-blue-darkest">
            {{ $t('performance_dashboard_staff_to_date') }}
            <info-button topic="chart_staff" @open="openInfo" />
          </h4>
          <p class="mb-2 text-center text-grey-darker">{{ $t('performance_dashboard_staff_to_date_subtitle') }}</p>
          <column-chart
            :data="staffChartData"
            :colors="[COLORS.planned, COLORS.actual]"
            :library="chartLibrary"
            :messages="{ empty: loadingPerformance ? $t('loading') : $t('performance_dashboard_no_chart_data') }"
            height="300px" />
          <p class="mt-2 text-grey-darker">
            {{ $t(weightByHours ? 'performance_dashboard_staff_to_date_note_takeoff' : 'performance_dashboard_staff_to_date_note') }}
          </p>
        </div>

        <div class="flex flex-col px-4 py-3 bg-white border border-grey-light rounded-lg">
          <h4 class="text-sm font-bold text-center text-blue-darkest">
            {{ $t('performance_dashboard_weeks_to_finish') }}
            <info-button topic="chart_weeks" @open="openInfo" />
          </h4>
          <p class="mb-2 text-center text-grey-darker">{{ $t('performance_dashboard_weeks_to_finish_subtitle') }}</p>
          <column-chart
            :data="weeksChartData"
            :colors="[COLORS.actual, COLORS.planned, COLORS.remaining]"
            :library="chartLibrary"
            :messages="{ empty: loadingPerformance ? $t('loading') : $t('performance_dashboard_no_chart_data') }"
            height="300px" />
          <p class="mt-2 text-grey-darker">
            {{ $t('performance_dashboard_weeks_rates_note', { planWeeks: performanceWindows.plan_weeks }) }}
          </p>
          <p class="mt-1 font-semibold text-red-dark">
            {{ $t('performance_dashboard_weeks_to_finish_note', { overdue: weeksCoverage.overdue }) }}
          </p>
        </div>
      </div>

      <!-- 2. Actividades que requieren atención -->
      <h3 class="mt-1 text-sm font-bold text-blue-darkest">
        {{ $t('performance_dashboard_section_attention') }}
        <info-button topic="attention" @open="openInfo" />
      </h3>

      <div v-if="canEditReview" class="flex items-center gap-2 px-3 py-2 border border-blue-lighter rounded bg-blue-lightest text-blue-darker">
        <i class="fas fa-pen"></i>
        <span>{{ $t('performance_dashboard_attention_edit_hint') }}</span>
      </div>

      <div class="epc-data-table">
        <div class="table-wrapper">
          <table class="table is-narrow">
            <thead>
              <tr>
                <th class="text-left">{{ $t('activity') }}</th>
                <th>{{ $t('performance_dashboard_col_critical_short') }}</th>
                <th>
                  {{ $t('performance_dashboard_col_priority_short') }}
                  <i v-if="canEditReview" class="ml-1 fas fa-pen text-xs" :title="$t('performance_dashboard_editable_column')"></i>
                </th>
                <th class="text-left">
                  {{ $t('performance_dashboard_col_situation') }}
                  <i v-if="canEditReview" class="ml-1 fas fa-pen text-xs" :title="$t('performance_dashboard_editable_column')"></i>
                </th>
                <th>{{ $t('performance_dashboard_col_actual_progress') }}</th>
                <th>{{ $t('performance_dashboard_col_budgeted_progress') }}</th>
                <th>{{ $t('performance_dashboard_col_days_late') }}</th>
                <th>{{ $t('performance_dashboard_col_float_days') }}</th>
                <th>{{ $t('performance_dashboard_col_project_delay_days') }}</th>
                <th>{{ $t('performance_dashboard_col_released_front_weeks') }}</th>
                <th v-for="column in REVIEW_TEXT_COLUMNS" :key="column" class="text-left">
                  {{ $t(column) }}
                  <i v-if="canEditReview" class="ml-1 fas fa-pen text-xs" :title="$t('performance_dashboard_editable_column')"></i>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!attentionActivities.length">
                <td colspan="14" class="py-6 text-center text-grey-dark">
                  {{ loadingPerformance ? $t('loading') : $t('no_results') }}
                </td>
              </tr>
              <tr v-for="activity in attentionActivities" :key="activity.id"
                :class="['cursor-pointer', { 'epc-data-table__row--selected': activity.code === selectedActivityCode }]"
                @click="selectActivity(activity.code)">
                <td class="w-56">
                  <div class="font-mono text-grey-dark">
                    {{ activity.code }}
                    <b-icon v-if="savingReviewIds[activity.id]" pack="fas" icon="spinner" size="is-small" custom-class="fa-spin" class="text-blue" />
                  </div>
                  <div class="font-bold uppercase text-blue-darkest">{{ activity.name }}</div>
                </td>
                <td class="text-center font-bold whitespace-nowrap">
                  <span :class="{ 'text-red-dark': activity.criticality !== 'no' }">{{ $t(CRITICALITY_LABELS[activity.criticality]) }}</span>
                </td>
                <!-- Prioridad, causa, acción, responsable y fecha se editan en la propia fila y se guardan al salir del campo -->
                <td :class="['text-center font-bold', PRIORITY_CLASSES[draftOf(activity).priority] || '']" @click.stop>
                  <select v-if="canEditReview" :value="draftOf(activity).priority"
                    :class="['px-1 py-0.5 font-bold border rounded cursor-pointer focus:outline-none focus:border-blue',
                             draftOf(activity).priority ? 'bg-transparent border-white' : 'bg-white border-grey text-grey-darkest hover:border-blue']"
                    style="color: inherit"
                    @change="setReviewDraft(activity, 'priority', $event.target.value, true)">
                    <option value="" class="text-grey-darkest">{{ $t('performance_dashboard_choose') }}</option>
                    <option value="p1" class="text-grey-darkest">P1</option>
                    <option value="p2" class="text-grey-darkest">P2</option>
                  </select>
                  <span v-else>{{ activity.priority ? activity.priority.toUpperCase() : '—' }}</span>
                </td>
                <td class="w-56 font-bold text-blue-darkest" @click.stop>
                  <textarea v-if="canEditReview" :value="draftOf(activity).situation" rows="2" maxlength="2000"
                    :placeholder="$t('performance_dashboard_click_to_write')"
                    :class="reviewInputClasses(activity, 'situation')"
                    @input="setReviewDraft(activity, 'situation', $event.target.value)"
                    @blur="saveReview(activity)" />
                  <span v-else-if="activity.situation">{{ activity.situation }}</span>
                  <span v-else class="font-normal text-grey-dark">—</span>
                </td>
                <td class="text-right font-bold whitespace-nowrap">
                  <span v-if="activity.progressFromField" class="text-grey-dark">F </span>
                  <span :class="{ 'text-red-dark': activity.actual != null && activity.baseline != null && activity.actual < activity.baseline }">
                    {{ fmtPercent(activity.actual, 0) }}
                  </span>
                </td>
                <td class="text-right">{{ fmtPercent(activity.baseline, 0) }}</td>
                <td class="text-right font-bold">
                  <span :class="{ 'text-red-dark': activity.grossDelay > 0 }">{{ fmtSignedDays(activity.grossDelay) }}</span>
                </td>
                <td class="text-right">
                  <span v-if="activity.float == null" :class="NOT_CALCULABLE_CLASSES" :title="$t('performance_dashboard_not_calculable_hint')">{{ $t('performance_dashboard_not_calculable_short') }}</span>
                  <template v-else>{{ fmt(activity.float, 0) }}</template>
                </td>
                <td :class="['text-right font-bold', { 'bg-orange-lightest text-red-dark': activity.projectDelay > 0 }]">
                  <span v-if="activity.grossDelay != null && activity.float == null" :class="NOT_CALCULABLE_CLASSES" :title="$t('performance_dashboard_not_calculable_hint')">
                    {{ $t('performance_dashboard_not_calculable_short') }}
                  </span>
                  <template v-else>{{ fmtSignedDays(activity.projectDelay) }}</template>
                </td>
                <td :class="['text-right', { 'bg-orange-lightest font-bold text-red-dark': activity.releasedFrontWeeks !== null && activity.releasedFrontWeeks < 1 }]">
                  <span v-if="activity.releasedFrontWeeks === null" :class="NOT_CALCULABLE_CLASSES" :title="$t('performance_dashboard_not_calculable_hint')">
                    {{ $t('performance_dashboard_not_calculable_short') }}
                  </span>
                  <template v-else>{{ fmt(activity.releasedFrontWeeks) }}</template>
                </td>
                <td class="w-56" @click.stop>
                  <textarea v-if="canEditReview" :value="draftOf(activity).cause" rows="2" maxlength="2000"
                    :placeholder="$t('performance_dashboard_click_to_write')"
                    :class="reviewInputClasses(activity, 'cause')"
                    @input="setReviewDraft(activity, 'cause', $event.target.value)"
                    @blur="saveReview(activity)" />
                  <span v-else-if="activity.cause">{{ activity.cause }}</span>
                  <span v-else class="text-grey-dark">—</span>
                </td>
                <td :class="['w-56', { 'bg-orange-lightest': !draftOf(activity).action }]" @click.stop>
                  <textarea v-if="canEditReview" :value="draftOf(activity).action" rows="2" maxlength="2000"
                    :placeholder="$t('performance_dashboard_no_action') + ' · ' + $t('performance_dashboard_click_to_write')"
                    :class="reviewInputClasses(activity, 'action')"
                    @input="setReviewDraft(activity, 'action', $event.target.value)"
                    @blur="saveReview(activity)" />
                  <span v-else-if="activity.action">{{ activity.action }}</span>
                  <span v-else class="text-grey-dark">— {{ $t('performance_dashboard_no_action') }} —</span>
                </td>
                <td class="w-32" @click.stop>
                  <input v-if="canEditReview" :value="draftOf(activity).manager" type="text" maxlength="255"
                    :placeholder="$t('performance_dashboard_click_to_write')"
                    :class="reviewInputClasses(activity, 'manager')"
                    @input="setReviewDraft(activity, 'manager', $event.target.value)"
                    @keyup.enter="$event.target.blur()"
                    @blur="saveReview(activity)">
                  <span v-else>{{ activity.actionManager || '—' }}</span>
                </td>
                <td class="whitespace-nowrap" @click.stop>
                  <input v-if="canEditReview" :value="draftOf(activity).date || ''" type="date"
                    :class="reviewInputClasses(activity, 'date')"
                    @change="setReviewDraft(activity, 'date', $event.target.value || null, true)">
                  <span v-else>{{ fmtDate(activity.actionDate) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <p class="italic text-grey-darker">{{ $t('performance_dashboard_attention_order') }}</p>
      <p v-if="attentionActivities.some(activity => activity.progressFromField)" class="italic text-grey-darker">
        {{ $t('performance_dashboard_attention_f_note') }}
      </p>
      <not-calculable-note :items="attentionNotCalculable" />

      <!-- 3. Por qué se analizan estas actividades: en verde el criterio que cumple -->
      <h3 class="mt-1 text-sm font-bold text-blue-darkest">
        {{ $t('performance_dashboard_section_reasons') }}
        <info-button topic="reasons" @open="openInfo" />
      </h3>
      <div class="epc-data-table">
        <div class="table-wrapper">
          <table class="table is-narrow">
            <thead>
              <tr>
                <th class="text-left">{{ $t('activity') }}</th>
                <th class="w-20">{{ $t('performance_dashboard_reason_critical_path') }}</th>
                <th class="w-24">{{ $t('performance_dashboard_reasons_col_cost_share') }}</th>
                <th class="w-56">{{ $t('performance_dashboard_reasons_col_deviation') }}</th>
                <th class="text-left">{{ $t('performance_dashboard_reason_followup') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!reasonRows.length">
                <td colspan="5" class="py-4 text-center text-grey-dark">{{ loadingPerformance ? $t('loading') : $t('no_results') }}</td>
              </tr>
              <tr v-for="row in reasonRows" :key="row.id"
                :class="['cursor-pointer', { 'epc-data-table__row--selected': row.code === selectedActivityCode }]"
                @click="selectActivity(row.code)">
                <td>
                  <span class="font-mono text-grey-dark">{{ row.code }}</span>
                  <span class="ml-1 uppercase">{{ row.name }}</span>
                </td>
                <td :class="['text-center font-bold', row.meetsCritical ? 'bg-green-lightest text-green-dark' : '']">
                  {{ $t(row.critical ? 'yes' : 'no') }}
                </td>
                <!-- En takeoff no hay coste: n/c -->
                <td :class="['text-center font-bold', row.meetsWeight && !weightByHours ? 'bg-green-lightest text-green-dark' : '']">
                  <span v-if="weightByHours" :class="NOT_CALCULABLE_CLASSES" :title="$t('performance_dashboard_not_calculable_hint')">{{ $t('performance_dashboard_not_calculable_short') }}</span>
                  <template v-else>{{ fmtPercent(row.costShare, 1) }}</template>
                </td>
                <td :class="['text-center font-bold', row.meetsDeviation ? 'bg-green-lightest text-green-dark' : '']">
                  <span v-if="weightByHours || row.costDeviation == null" :class="NOT_CALCULABLE_CLASSES" :title="$t('performance_dashboard_not_calculable_hint')">
                    {{ $t('performance_dashboard_not_calculable_short') }}
                  </span>
                  <template v-else>{{ fmtSignedPercent(row.costDeviation) }}</template>
                </td>
                <td>{{ row.text }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <p class="italic text-grey-darker">{{ $t('performance_dashboard_reasons_note') }}</p>
      <not-calculable-note :items="reasonsNotCalculable" />

      <!-- 4. Ficha de evolución de actividad -->
      <div v-if="selectedActivity" class="flex flex-col gap-3 px-4 py-3 mt-1 bg-white border border-grey-light rounded-lg">
        <div>
          <h3 class="text-base font-bold text-blue-darkest">
            {{ $t('performance_dashboard_section_activity_sheet') }}
            <info-button topic="sheet" @open="openInfo" />
          </h3>
          <p class="italic text-grey-darker">{{ $t('performance_dashboard_sheet_choose_hint') }}</p>
        </div>

        <!-- Actividad (celda amarilla = selector) · unidad · partida · ruta crítica | prioridad -->
        <div class="flex flex-wrap items-center gap-2">
          <span class="px-2 py-1 font-bold text-white rounded bg-blue-darkest">{{ $t('activity') }}</span>
          <select v-model="selectedActivityCode" class="px-2 py-1 font-mono font-bold border rounded cursor-pointer bg-yellow-lighter border-yellow focus:outline-none">
            <option v-for="activity in attentionActivities" :key="activity.id" :value="activity.code">
              {{ activity.code }} · {{ activity.name.toUpperCase() }}
            </option>
          </select>
          <span class="text-sm font-bold text-blue-darkest">
            {{ selectedActivity.name }}
            · {{ $t('unit') }}: {{ sheet.unit || '—' }}
            · {{ $t('performance_dashboard_sheet_boq_item') }}:
            <span v-if="selectedActivity.boqCode">{{ selectedActivity.boqCode }}</span>
            <span v-else :class="NOT_CALCULABLE_CLASSES" :title="$t('performance_dashboard_not_calculable_hint')">{{ $t('performance_dashboard_not_calculable_short') }}</span>
            <template v-if="selectedActivity.critical">· {{ $t('performance_dashboard_sheet_critical_path') }}</template>
          </span>
          <div class="flex items-stretch ml-auto overflow-hidden rounded">
            <span class="px-3 py-1 font-bold text-white bg-blue-darkest">{{ $t('performance_dashboard_col_priority') }}</span>
            <span :class="['px-6 py-1 font-bold text-center', PRIORITY_CLASSES[selectedActivity.priority] || 'bg-grey-lighter text-grey-darker']">
              {{ selectedActivity.priority ? selectedActivity.priority.toUpperCase() : $t('performance_dashboard_not_defined') }}
            </span>
          </div>
        </div>

        <p class="italic text-grey-darker">
          {{ sheetSubtitle }}
          <template v-if="selectedActivity.measurements.length > 1">
            ·
            {{ $t('measurement') }}:
            <select class="px-1 py-0.5 text-xs not-italic bg-white border border-grey-light rounded"
              :value="selectedActivity.selectedMeasurementId != null ? String(selectedActivity.selectedMeasurementId) : ''"
              @change="setAttentionMeasurement(selectedActivity.id, $event.target.value)">
              <option v-if="!selectedActivity.hasPrimary" value="">—</option>
              <option v-for="measurement in selectedActivity.measurements" :key="measurement.id" :value="String(measurement.id)">
                {{ measurement.primary ? '★ ' : '' }}{{ measurement.name }}
              </option>
            </select>
          </template>
        </p>

        <!-- Indicadores de la actividad -->
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          <div v-for="kpi in sheetKpis" :key="kpi.label" class="px-4 py-3 rounded bg-grey-lightest">
            <div class="font-bold uppercase tracking-wide text-grey-darker">
              {{ kpi.label }}
              <info-button :topic="kpi.info" @open="openInfo" />
            </div>
            <div :class="['mt-1 text-2xl font-bold leading-tight', kpi.negative ? 'text-red-dark' : 'text-blue-darkest']">
              <span v-if="kpi.notCalculable" :class="NOT_CALCULABLE_CLASSES" :title="$t('performance_dashboard_not_calculable_hint')">{{ $t('performance_dashboard_not_calculable_short') }}</span>
              <template v-else>{{ kpi.value }}</template>
            </div>
            <div class="mt-1 text-grey-darker">{{ kpi.hint }}</div>
          </div>
        </div>

        <!-- A pasado / A futuro -->
        <div class="grid grid-cols-1 xl:grid-cols-2 gap-3">
          <div class="epc-data-table">
            <table class="table is-narrow">
              <thead>
                <tr><th colspan="4">{{ $t('performance_dashboard_past_title') }} <info-button topic="past" light @open="openInfo" /></th></tr>
                <tr>
                  <th></th>
                  <th>{{ $t('performance_dashboard_col_budgeted_short') }}</th>
                  <th>{{ $t('performance_dashboard_col_planned_short') }}</th>
                  <th>{{ $t('actual') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in pastRows" :key="row.label">
                  <td class="font-bold">{{ row.label }}</td>
                  <td v-for="(cell, i) in row.cells" :key="i" :class="['text-right', { 'font-bold': i === 2 }]">
                    <span v-if="cell.notCalculable" :class="NOT_CALCULABLE_CLASSES" :title="$t('performance_dashboard_not_calculable_hint')">{{ $t('performance_dashboard_not_calculable_short') }}</span>
                    <span v-else :class="{ 'text-red-dark': cell.alert }">{{ cell.text }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="epc-data-table">
            <table class="table is-narrow">
              <thead>
                <tr><th colspan="5">{{ $t('performance_dashboard_future_title', { weeks: fmt(selectedActivity.weeksToEnd) }) }} <info-button topic="future" light @open="openInfo" /></th></tr>
                <tr>
                  <th></th>
                  <th>{{ $t('performance_dashboard_col_remaining_budget') }}</th>
                  <th>{{ $t('performance_dashboard_col_plan_6_weeks') }}</th>
                  <th>{{ $t('performance_dashboard_col_needed') }}</th>
                  <th>{{ $t('performance_dashboard_col_actual_4_weeks') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in futureRows" :key="row.label">
                  <td class="font-bold">{{ row.label }}</td>
                  <td v-for="(cell, i) in row.cells" :key="i" :class="['text-right', { 'font-bold': i === 2 }]">
                    <span v-if="cell.notCalculable" :class="NOT_CALCULABLE_CLASSES" :title="$t('performance_dashboard_not_calculable_hint')">{{ $t('performance_dashboard_not_calculable_short') }}</span>
                    <span v-else :class="{ 'font-bold text-red-dark': cell.alert }">{{ cell.text }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <p class="italic text-grey-darker">{{ $t('performance_dashboard_sheet_note') }}</p>

        <!-- Situación / causa / acción (se editan en la tabla de atención) -->
        <table class="w-full border border-grey-light">
          <tbody>
            <tr v-for="row in sheetReviewRows" :key="row.label" class="border-b border-grey-light">
              <td class="w-40 px-3 py-1 font-bold text-center text-white bg-blue-darkest">
                {{ row.label }}
                <info-button v-if="row.info" :topic="row.info" light @open="openInfo" />
              </td>
              <td :class="['px-3 py-1', { 'font-bold': row.bold }]">
                <span v-if="row.text">{{ row.text }}</span>
                <span v-else class="font-normal text-grey-dark">{{ row.empty }}</span>
              </td>
            </tr>
          </tbody>
        </table>
        <not-calculable-note :items="sheetNotCalculable" />

        <p v-if="loadingActivityCharts" class="text-grey-darker">
          <b-icon pack="fas" icon="spinner" size="is-small" custom-class="fa-spin" />
          {{ $t('loading') }}…
        </p>

        <!-- Últimas 6 semanas del proyecto: barras de presupuestado, planificado y real (solo las calculables) -->
        <p class="font-bold text-blue-darkest">{{ $t('performance_dashboard_last_weeks', { weeks: weeklyWindow.length }) }}</p>
        <div class="grid grid-cols-1 xl:grid-cols-3 gap-3">
          <div v-for="chart in weeklyCharts" :key="chart.title" class="px-3 py-2 border border-grey-light rounded">
            <h4 class="mb-1 text-sm font-bold text-center text-blue-darkest">{{ chart.title }} <info-button :topic="chart.info" @open="openInfo" /></h4>
            <column-chart
              :data="chart.data"
              :colors="chart.colors"
              :library="chartLibrary"
              :messages="chartMessages"
              height="200px" />
          </div>
        </div>

        <!-- Histórico mensual -->
        <p class="font-bold text-blue-darkest">{{ $t('performance_dashboard_history_title', { start: workMonthOrigin }) }}</p>
        <div class="grid grid-cols-1 xl:grid-cols-3 gap-3">
          <div v-for="chart in historyCharts" :key="chart.title" class="px-3 py-2 border border-grey-light rounded">
            <h4 class="mb-1 text-sm font-bold text-center text-blue-darkest">{{ chart.title }} <info-button :topic="chart.info" @open="openInfo" /></h4>
            <line-chart :data="chart.data" :colors="chart.colors" :library="historyLibrary" :messages="chartMessages" height="220px" />
          </div>
        </div>

        <!-- Evolución mensual: desde el inicio; si hay más de 12 meses de obra, solo los últimos 12 -->
        <p class="font-bold text-blue-darkest">{{ $t('performance_dashboard_monthly_title') }} <info-button topic="monthly" @open="openInfo" /></p>
        <div class="epc-data-table">
          <div class="table-wrapper">
            <table class="table is-narrow">
              <thead>
                <tr>
                  <th rowspan="2" class="text-left">{{ $t('performance_dashboard_col_work_month') }}</th>
                  <th colspan="3">{{ $t('performance_dashboard_col_progress_unit', { unit: sheet.unit }) }}</th>
                  <th colspan="2">{{ $t('performance_dashboard_col_cumulative_progress') }}</th>
                  <th colspan="3">{{ $t('performance_dashboard_row_staff') }}</th>
                  <th colspan="3">{{ $t('performance_dashboard_row_yield', { unit: sheet.unit }) }}</th>
                  <th>{{ $t('performance_dashboard_col_losses') }}</th>
                </tr>
                <tr>
                  <th>{{ $t('performance_dashboard_col_budgeted_short') }}</th>
                  <th>{{ $t('performance_dashboard_col_planned_short') }}</th>
                  <th>{{ $t('actual') }}</th>
                  <th>{{ $t('performance_dashboard_col_budgeted_short') }}</th>
                  <th>{{ $t('actual') }}</th>
                  <th>{{ $t('performance_dashboard_col_budgeted_short') }}</th>
                  <th>{{ $t('performance_dashboard_col_planned_short') }}</th>
                  <th>{{ $t('actual') }}</th>
                  <th>{{ $t('performance_dashboard_col_budgeted_short') }}</th>
                  <th>{{ $t('performance_dashboard_col_planned_short') }}</th>
                  <th>{{ $t('actual') }}</th>
                  <th>{{ $t('performance_dashboard_col_lost_hours') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!monthlyEvolution.length">
                  <td colspan="13" class="text-grey-dark">{{ $t('performance_dashboard_no_chart_data') }}</td>
                </tr>
                <tr v-for="month in monthlyEvolution" :key="month.number">
                  <td class="font-bold text-center whitespace-nowrap">{{ month.number }}</td>
                  <td v-for="(cell, i) in month.cells" :key="i" :class="['text-right', { 'text-center': i === month.cells.length - 1 }]">
                    <span v-if="cell.notCalculable" :class="NOT_CALCULABLE_CLASSES" :title="$t('performance_dashboard_not_calculable_hint')">{{ $t('performance_dashboard_not_calculable_short') }}</span>
                    <span v-else :class="{ 'font-bold text-red-dark': cell.alert }">{{ cell.text }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <not-calculable-note :items="monthlyNotCalculable" />
      </div>
    </div>

    <!-- Detalle técnico del bloque (icono «i») -->
    <b-modal :active="infoDetail !== null" has-modal-card @close="infoTopic = null">
      <div v-if="infoDetail" class="modal-card" style="width: 760px; max-width: 95vw">
        <header class="modal-card-head">
          <p class="modal-card-title text-base font-bold">{{ infoDetail.title }}</p>
          <button type="button" class="delete" @click="infoTopic = null" />
        </header>
        <section class="modal-card-body">
          <template v-if="infoDetail.summary">
            <h4 class="mb-1 text-sm font-bold text-blue-darkest">{{ PERFORMANCE_INFO_SECTIONS.summary }}</h4>
            <p class="mb-4 text-sm leading-relaxed whitespace-pre-line text-grey-darkest">{{ infoDetail.summary }}</p>
          </template>
          <h4 class="mb-1 text-sm font-bold text-blue-darkest">{{ PERFORMANCE_INFO_SECTIONS.detail }}</h4>
          <pre class="p-0 text-xs leading-relaxed whitespace-pre-wrap bg-white text-grey-darkest">{{ infoDetail.text }}</pre>
        </section>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Vue from 'vue'
import axios from 'axios'
import moment from 'moment'
import { mapGetters } from 'vuex'
import VueChartkick from 'vue-chartkick'
import Chart from 'chart.js'
import PageHeader from '·/components/page-header'
import { buildProjectWeeks, findWeekIndexForDate } from './activities/weekly/weeklyProjectWeeks.js'
import { PERFORMANCE_INFO, PERFORMANCE_INFO_SECTIONS, PERFORMANCE_INFO_SUMMARY, PERFORMANCE_INFO_TITLE } from './dashboard-performance-info'

Vue.use(VueChartkick, { adapter: Chart })

// Icono «i» que abre el detalle técnico del bloque (dashboard-performance-info.js)
const InfoButton = {
  name: 'InfoButton',
  props: {
    topic: { type: String, required: true },
    light: { type: Boolean, default: false },
  },
  render(h) {
    return h('button', {
      attrs: { type: 'button', title: PERFORMANCE_INFO_TITLE },
      class: ['inline-flex items-center ml-1 align-middle focus:outline-none', this.light ? 'text-white hover:text-blue-lighter' : 'text-blue hover:text-blue-darker'],
      on: {
        click: (event) => {
          event.preventDefault()
          event.stopPropagation()
          this.$emit('open', this.topic)
        },
      },
    }, [h('i', { class: 'fas fa-info-circle' })])
  },
}

// Nota bajo una tabla con lo que no se puede calcular con los datos del proyecto y por qué
const NotCalculableNote = {
  name: 'NotCalculableNote',
  props: {
    items: { type: Array, default: () => [] },
  },
  render(h) {
    if (!this.items.length) return null
    return h('div', { class: 'px-3 py-2 border border-yellow rounded bg-yellow-lightest text-yellow-darkest' }, [
      h('div', { class: 'mb-1 font-bold' }, [
        h('i', { class: 'mr-1 fas fa-exclamation-triangle' }),
        this.$t('performance_dashboard_not_calculable_title'),
      ]),
      h('ul', { class: 'pl-4 list-disc' }, this.items.map(text => h('li', { key: text }, text))),
    ])
  },
}

// Marca «n/c» de una celda que no se puede calcular (la razón está en NotCalculableNote)
const NOT_CALCULABLE_CLASSES = 'italic font-normal text-grey-dark border-b border-dotted border-grey cursor-help'

const COLORS = {
  budgeted: '#9ca3af',
  planned: '#3a6bc8',
  actual: '#e8803a',
  remaining: '#e3342f',
}

const FOOTER_VARIANTS = {
  warning: 'bg-yellow-lightest text-yellow-darker',
  success: 'bg-green-lightest text-green-dark',
  danger: 'bg-red-lightest text-red-dark',
}

// Variante del pie de cada indicador → color del semáforo que se escribe
const TRAFFIC_LIGHTS = {
  danger: 'performance_dashboard_light_red',
  warning: 'performance_dashboard_light_yellow',
  success: 'performance_dashboard_light_green',
}

// Columna «Crítica»: no estando en ruta crítica, pasa a sí cuando el retraso bruto se come la holgura
const CRITICALITY_LABELS = {
  yes: 'yes',
  becomes_yes: 'performance_dashboard_critical_becomes_yes',
  no: 'no',
}

const PRIORITY_ORDER = { p1: 0, p2: 1, none: 2 }

const PRIORITY_CLASSES = {
  p1: 'bg-red-dark text-white',
  p2: 'bg-orange text-grey-darkest',
}

// Campos editables de la tabla de atención: con borde y fondo de campo siempre visibles para que se vea que se escriben
const REVIEW_INPUT_CLASSES = 'w-full px-2 py-1 text-xs font-normal text-grey-darkest border rounded shadow-inner cursor-text resize-y hover:border-blue focus:border-blue focus:outline-none'

// Columnas de texto editables al final de la tabla de atención (cabeceras con el lápiz)
const REVIEW_TEXT_COLUMNS = [
  'performance_dashboard_col_cause_project',
  'performance_dashboard_agreed_action',
  'performance_dashboard_col_responsible',
  'date',
]

// Jornada por defecto (h/día) si el proyecto no define configs.man_hour, igual que la planificación semanal
const DEFAULT_MAN_HOUR = 8

const emptyReview = () => ({ priority: '', situation: '', cause: '', action: '', manager: '', date: null, status: 'pending' })
export default {
  middleware: 'auth',
  title: 'project_home_page_title',
  subtitle: 'project_home_page_subtitle',
  menuOption: '2-1',
  hidePageTitle: true,

  components: {
    PageHeader,
    InfoButton,
    NotCalculableNote,
  },

  data: () => ({
    PERFORMANCE_INFO_SECTIONS,
    COLORS,
    FOOTER_VARIANTS,
    CRITICALITY_LABELS,
    PRIORITY_CLASSES,
    REVIEW_TEXT_COLUMNS,
    NOT_CALCULABLE_CLASSES,

    // Actividades padre de la semana seleccionadas por el criterio, desde el backend
    performanceActivities: [],
    performanceWindows: { plan_weeks: 6 },
    performanceThresholds: { weight: null, deviation: null },
    // 'cost': peso por coste de ejecución (€); 'man_hours': por HH programadas (modo takeoff)
    weightBasis: 'cost',
    performanceSummary: {
      selected: 0,
      total_cost: 0,
      selected_cost: 0,
    },
    // id de actividad → id de la medición padre elegida (por defecto la primaria, si existe)
    attentionMeasurementIds: {},
    loadingPerformance: false,
    // Series pesadas (histórico / weekly_yield / series) se cargan al abrir el detalle
    loadingActivityCharts: false,
    activityChartsLoaded: {},

    selectedActivityCode: null,
    // id de actividad → revisión editada en la tabla de atención y aún no guardada
    reviewDrafts: {},
    savingReviewIds: {},
    exportingPdf: false,
    // Bloque cuyo detalle técnico está abierto (clave de PERFORMANCE_INFO)
    infoTopic: null,
  }),

  computed: {
    ...mapGetters({
      project: 'app/project',
    }),

    cutoffDate() {
      return moment().format('DD/MM/YYYY')
    },

    // Semana del proyecto, sus fechas y el proyecto
    headerSubtitle() {
      const week = this.projectWeek
      const project = this.project ? this.project.name : ''
      if (!week) return project
      return this.$t('performance_dashboard_weekly_summary_subtitle', {
        week: week.number,
        start: week.start.format('DD/MM/YY'),
        end: week.end.format('DD/MM/YY'),
        project,
      })
    },

    // «Se analizan N actividades · X % del coste · N en ruta crítica · N con su partida desviada…»
    // En takeoff no hay coste ni informe de control: n/c (la razón está en la nota de la tabla 3)
    analyzedSummary() {
      const activities = this.attentionActivities
      const notCalculable = this.$t('performance_dashboard_not_calculable_short')
      return this.$t('performance_dashboard_analyzed_summary', {
        count: this.performanceSummary.selected,
        percent: this.weightByHours ? notCalculable : this.fmtPercent(this.selectionCoveredPercent, 0),
        critical: activities.filter(activity => activity.critical).length,
        deviated: this.weightByHours ? notCalculable : activities.filter(activity => activity.reasons.includes('cost_deviation')).length,
        deviation: this.performanceThresholds.deviation,
      })
    },

    // Mismo cálculo de semanas que la planificación semanal (weekly.vue)
    projectWeeks() {
      if (!this.project) return []
      return buildProjectWeeks(this.project.current_start_date, this.project.current_end_date)
    },

    // Las 6 semanas del proyecto que terminan en la semana de corte (S7, S8…)
    weeklyWindow() {
      const weeks = this.projectWeeks
      const current = this.projectWeek
      if (!weeks.length || !current) return []
      const end = weeks.findIndex(week => week.number === current.number && week.year === current.year)
      const last = end === -1 ? weeks.length - 1 : end
      return weeks.slice(Math.max(0, last - 5), last + 1)
    },

    // Mes 1 del histórico: el mes en que empieza el proyecto
    workMonthOrigin() {
      const start = this.project && this.project.current_start_date
      return start ? moment(start).format('MM/YYYY') : '—'
    },

    // Semana que contiene la fecha de corte (hoy); si el proyecto ya terminó, la última
    projectWeek() {
      const weeks = this.projectWeeks
      if (!weeks.length) return null
      const today = moment()
      if (today.isAfter(weeks[weeks.length - 1].end, 'day')) return weeks[weeks.length - 1]
      return weeks[findWeekIndexForDate(weeks, today)]
    },

    infoDetail() {
      const detail = this.infoTopic ? PERFORMANCE_INFO[this.infoTopic] : null
      return detail ? Object.assign({ summary: PERFORMANCE_INFO_SUMMARY[this.infoTopic] || '' }, detail) : null
    },

    // Fila de la tabla de motivos: cada criterio con su valor y si lo cumple
    reasonRows() {
      return this.attentionActivities.map((activity) => {
        const reasons = activity.reasons || []
        const texts = reasons.map((reason) => {
          if (reason === 'weight') {
            return this.$t(this.weightByHours ? 'performance_dashboard_reason_text_weight_hours' : 'performance_dashboard_reason_text_weight', { share: this.fmtPercent(activity.weight, 1) })
          }
          if (reason === 'cost_deviation') return this.$t('performance_dashboard_reason_text_cost_deviation', { deviation: this.fmtSignedPercent(activity.costDeviation) })
          return this.$t('performance_dashboard_reason_text_' + reason)
        })
        return {
          id: activity.id,
          code: activity.code,
          name: activity.name,
          critical: !!activity.critical,
          costShare: activity.weight,
          costDeviation: activity.costDeviation,
          meetsCritical: reasons.includes('critical_path'),
          meetsWeight: reasons.includes('weight'),
          meetsDeviation: reasons.includes('cost_deviation'),
          text: texts.join(', '),
        }
      })
    },

    weightByHours() {
      return this.weightBasis === 'man_hours'
    },

    selectionCoveredPercent() {
      const total = this.performanceSummary.total_cost
      return total > 0 ? (this.performanceSummary.selected_cost * 100) / total : null
    },

    // Indicadores de cabecera sobre las actividades seleccionadas y sus revisiones guardadas
    indicators() {
      const activities = this.attentionActivities
      const today = moment().format('YYYY-MM-DD')
      const withDelay = activities.filter(activity => activity.sortDelay !== null && activity.sortDelay !== undefined)
      const worst = withDelay.reduce((max, activity) => (max === null || activity.sortDelay > max.sortDelay ? activity : max), null)
      return {
        priority: {
          p1: activities.filter(activity => activity.priority === 'p1').length,
          p2: activities.filter(activity => activity.priority === 'p2').length,
        },
        projectDelay: {
          days: worst ? worst.sortDelay : null,
          code: worst ? worst.code : '',
          delaying: withDelay.filter(activity => activity.sortDelay > 0).length,
        },
        overdueActions: activities
          .filter(activity => activity.action && activity.actionDate && activity.actionDate < today && activity.actionStatus !== 'completed')
          .length,
      }
    },

    // Semáforo: rojo = hay que actuar, amarillo = aviso o sin dato, verde = bien
    kpis() {
      const { priority, projectDelay, overdueActions } = this.indicators
      const light = (variant, text) => [this.$t(TRAFFIC_LIGHTS[variant]), text].filter(Boolean).join(' · ')

      let priorityVariant = 'success'
      let priorityText = ''
      if (priority.p1) {
        priorityVariant = 'danger'
        priorityText = this.$t('performance_dashboard_light_has_p1')
      } else if (priority.p2) {
        priorityVariant = 'warning'
        priorityText = this.$t('performance_dashboard_light_has_p2')
      }

      let delayVariant = 'success'
      let delayText = ''
      if (projectDelay.days === null) {
        delayVariant = 'warning'
        delayText = this.$t('performance_dashboard_light_no_delay_data')
      } else if (projectDelay.delaying) {
        delayVariant = 'danger'
        delayText = this.$t('performance_dashboard_light_delaying', { count: projectDelay.delaying })
      }
      const delayValue = projectDelay.days === null
        ? '—'
        : `${projectDelay.days > 0 ? '+' : ''}${this.$t('performance_dashboard_days', { count: projectDelay.days })} · ${projectDelay.code}`

      return [
        {
          label: this.$t('performance_dashboard_kpi_priority_activities'),
          info: 'kpi_priority',
          value: `${priority.p1} / ${priority.p2}`,
          footer: light(priorityVariant, priorityText),
          variant: priorityVariant,
        },
        {
          label: this.$t('performance_dashboard_kpi_project_delay_current_rate'),
          info: 'kpi_max_delay',
          value: delayValue,
          footer: light(delayVariant, delayText),
          variant: delayVariant,
        },
        {
          label: this.$t('performance_dashboard_kpi_overdue_agreed_actions'),
          info: 'kpi_overdue_actions',
          value: this.fmt(overdueActions, 0),
          footer: light(overdueActions ? 'danger' : 'success', overdueActions ? this.$t('performance_dashboard_overdue_count', { count: overdueActions }) : ''),
          variant: overdueActions ? 'danger' : 'success',
        },
      ]
    },

    // Presupuestado = línea base (fechas programadas), Planificado = última planificación, Real = avance de la actividad
    progressChartData() {
      if (!this.performanceActivities.length) return []
      return [
        { name: this.$t('performance_dashboard_budgeted'), data: this.performanceSeriesOf(a => a.progress.budgeted) },
        { name: this.$t('planned'), data: this.performanceSeriesOf(a => a.progress.planned) },
        { name: this.$t('actual'), data: this.performanceSeriesOf(a => a.progress.actual) },
      ]
    },

    // Personas medias a origen; el presupuestado no existe en el sistema y no se dibuja
    staffChartData() {
      if (!this.staffCoverage.actual && !this.staffCoverage.planned) return []
      return [
        { name: this.$t('planned'), data: this.performanceSeriesOf(a => a.staff.planned) },
        { name: this.$t('actual'), data: this.performanceSeriesOf(a => a.staff.actual) },
      ]
    },

    staffCoverage() {
      return {
        actual: this.performanceActivities.filter(a => a.staff.actual !== null).length,
        planned: this.performanceActivities.filter(a => a.staff.planned !== null).length,
      }
    },

    weeksCoverage() {
      const activities = this.performanceActivities
      const weeksOf = activity => activity.weeks || {}
      return {
        overdue: activities.filter(activity => weeksOf(activity).remaining < 0).length,
      }
    },

    weeksChartData() {
      const hasValues = this.performanceActivities.some((activity) => {
        const weeks = activity.weeks || {}
        return weeks.current !== null && weeks.current !== undefined
          || weeks.plan !== null && weeks.plan !== undefined
          || weeks.remaining !== null && weeks.remaining !== undefined
      })
      if (!hasValues) return []
      return [
        { name: this.$t('performance_dashboard_current_rate'), data: this.performanceSeriesOf(a => a.weeks.current) },
        { name: this.$t('performance_dashboard_plan_rate', { weeks: this.performanceWindows.plan_weeks }), data: this.performanceSeriesOf(a => a.weeks.plan) },
        {
          name: this.$t('performance_dashboard_remaining_weeks_plan'),
          data: this.performanceSeriesOf(a => a.weeks.remaining),
          // Raya horizontal (sin línea entre puntos) superpuesta a las barras
          dataset: {
            type: 'line',
            showLine: false,
            fill: false,
            pointStyle: 'line',
            pointRadius: 14,
            pointHoverRadius: 14,
            borderWidth: 2,
            borderColor: COLORS.remaining,
            backgroundColor: COLORS.remaining,
          },
        },
      ]
    },

    chartLibrary() {
      return {
        legend: { display: true, position: 'bottom', labels: { boxWidth: 10, fontSize: 10 } },
        scales: {
          xAxes: [{ ticks: { fontSize: 9, maxRotation: 0, autoSkip: false } }],
          yAxes: [{ ticks: { fontSize: 10 } }],
        },
      }
    },

    chartMessages() {
      return { empty: this.$t('performance_dashboard_no_chart_data') }
    },

    // Las mismas actividades de las gráficas, ordenadas por el retraso del frente primario (sin dato al final).
    // Ejecutado, pendiente, ritmos y retraso de la fila salen de la medición padre elegida en el select.
    attentionActivities() {
      return this.performanceActivities
        .map((activity) => {
          const measurements = activity.measurements || []
          const selectedId = this.attentionMeasurementIds[activity.id]
          const selected = measurements.find((measurement) => measurement.measurement_id === selectedId) || null
          const weeks = activity.weeks || {}
          const progress = activity.progress || {}
          const review = activity.review || {}
          const mapped = {
            id: activity.id,
            code: activity.code,
            name: activity.name,
            reasons: activity.reasons || [],
            weight: activity.cost_share,
            costDeviation: activity.cost_deviation,
            baseline: progress.budgeted,
            lastPlan: progress.planned,
            actual: progress.actual,
            progressFromField: !!activity.progress_from_field,
            measurements: measurements.map((measurement) => ({
              id: measurement.measurement_id,
              name: measurement.takeoff ? this.$t('performance_dashboard_takeoff_measurement') : measurement.name,
              primary: !!measurement.primary,
              unit: measurement.unit || '',
              total: measurement.total,
              pending: measurement.pending,
              yieldActual: measurement.yield_actual,
              yield4w: measurement.yield_4w,
              yieldPlanned: measurement.yield_planned,
              weeklyYield: measurement.weekly_yield || [],
              series: measurement.series || [],
            })),
            hasPrimary: measurements.some((measurement) => measurement.primary),
            selectedMeasurementId: selected ? selected.measurement_id : null,
            pending: selected ? selected.pending : null,
            unit: selected && selected.unit ? selected.unit : '',
            rateRecent: selected && selected.rate_recent != null ? selected.rate_recent : null,
            plan6w: selected ? selected.plan_6w : null,
            endDate: activity.current_end_date,
            scheduledEndDate: activity.scheduled_end_date,
            boqCode: activity.boq_code || null,
            weeksToEnd: weeks.remaining,
            float: activity.float,
            critical: activity.critical_path,
            grossDelay: selected ? selected.gross_delay : null,
            projectDelay: selected ? selected.project_delay : null,
            sortDelay: activity.project_delay,
            staff: {
              budgeted: activity.staff ? activity.staff.budgeted : null,
              planned: activity.staff ? activity.staff.planned : null,
              actual: activity.staff ? activity.staff.actual : null,
            },
            sheetData: activity.sheet || {},
            monthlyHistory: activity.monthly_history || [],
            priority: review.priority || null,
            situation: review.situation || '',
            cause: review.cause || '',
            action: review.action || '',
            actionManager: review.action_manager || '',
            actionDate: review.action_date || null,
            actionStatus: review.action_status || null,
            // Sin dato de tajo liberado en el sistema: la columna sale como n/c
            releasedFrontWeeks: null,
          }
          mapped.criticality = this.criticalityOf(mapped)
          return mapped
        })
        // Por prioridad (P1, P2, sin prioridad) y, dentro de cada una, por retraso del proyecto (sin dato al final)
        .sort((a, b) => {
          const rank = activity => (activity.priority in PRIORITY_ORDER ? PRIORITY_ORDER[activity.priority] : PRIORITY_ORDER.none)
          const byPriority = rank(a) - rank(b)
          if (byPriority !== 0) return byPriority
          if (a.sortDelay === null && b.sortDelay === null) return 0
          if (a.sortDelay === null) return 1
          if (b.sortDelay === null) return -1
          return b.sortDelay - a.sortDelay
        })
    },

    // Gráficas en el mismo orden que la tabla de atención
    orderedPerformanceActivities() {
      const byId = {}
      this.performanceActivities.forEach((activity) => { byId[activity.id] = activity })
      return this.attentionActivities.map(activity => byId[activity.id]).filter(Boolean)
    },

    attentionNotCalculable() {
      const items = [this.$t('performance_dashboard_nc_released_front')]
      if (this.attentionActivities.some(activity => activity.float == null)) {
        items.push(this.$t('performance_dashboard_nc_float'))
      }
      return items
    },

    reasonsNotCalculable() {
      const items = this.weightByHours
        ? [this.$t('performance_dashboard_nc_cost_takeoff'), this.$t('performance_dashboard_nc_deviation_takeoff')]
        : [this.$t('performance_dashboard_nc_deviation_control_report')]
      items.push(this.$t('performance_dashboard_nc_risk'))
      return items
    },

    selectedActivity() {
      if (!this.selectedActivityCode) return null
      return this.attentionActivities.find(activity => activity.code === this.selectedActivityCode) || null
    },

    // Jornada del proyecto (h/día), como en la planificación semanal
    projectManHour() {
      const configs = this.project && this.project.configs
      const value = configs && configs.man_hour != null && configs.man_hour !== '' ? Number(configs.man_hour) : null
      return value && value > 0 ? value : DEFAULT_MAN_HOUR
    },


    /**
     * Ficha de la actividad elegida con la medición elegida.
     * A pasado: avance a origen, personas (planificadas = asignadas a la actividad) y h/ud plan / real.
     * A futuro:
     *  - ritmo necesario = pendiente / semanas hasta el fin;
     *  - personas necesarias = pendiente × h/ud real de las últimas 4 semanas / (días laborables que quedan × jornada);
     *  - h/ud necesario = HH programadas que quedan / pendiente. HH que quedan = personas planificadas × días que quedan × jornada,
     *    o HH programadas − HH registradas si la actividad no tiene personas.
     */
    sheet() {
      const a = this.selectedActivity
      if (!a) return null
      const m = this.measurementOf(a)
      const s = a.sheetData || {}
      const takeoff = this.weightByHours
      const manHour = s.man_hour || this.projectManHour
      const total = m ? m.total : null
      const pending = m ? m.pending : null
      const remainingDays = s.remaining_days || 0
      const plannedPeople = a.staff.planned
      const yieldPlan = m ? m.yieldPlanned : null
      // En takeoff las HH programadas de las etapas (índice HH ajustado del stage) se toman como planificadas: no hay h/ud presupuestado
      const yieldBudgeted = null
      const yieldPlanned = yieldPlan

      let remainingHours = null
      if (plannedPeople > 0 && remainingDays > 0) {
        remainingHours = plannedPeople * remainingDays * manHour
      } else if (s.scheduled_man_hours != null) {
        remainingHours = Math.max(s.scheduled_man_hours - (s.registered_man_hours || 0), 0)
      }

      // Presupuestado restante: lo que le falta a la línea base, repartido hasta su fecha fin
      const baselineWeeks = a.scheduledEndDate ? moment(a.scheduledEndDate).diff(moment().startOf('day'), 'days') / 7 : null
      const budgetedRemainingRate = total != null && a.baseline != null && baselineWeeks > 0
        ? (total * (100 - a.baseline)) / 100 / baselineWeeks
        : null
      const neededRate = pending !== null && a.weeksToEnd > 0 ? pending / a.weeksToEnd : null

      return {
        unit: m ? m.unit : '',
        pending,
        // Celdas sin dato en el sistema: se muestran como n/c y su razón va en sheetNotCalculable
        notCalculable: {
          budgetedStaff: true,
          budgetedYield: true,
          plannedYield: false,
          recentStaffYield: takeoff,
        },
        past: {
          progress: { budgeted: a.baseline, planned: a.lastPlan, actual: a.actual },
          staff: { budgeted: null, planned: plannedPeople, actual: a.staff.actual },
          yield: { budgeted: yieldBudgeted, planned: yieldPlanned, actual: m ? m.yieldActual : null },
        },
        future: {
          rate: {
            remaining: budgetedRemainingRate,
            plan6w: a.plan6w,
            needed: neededRate,
            real4w: a.rateRecent,
          },
          staff: {
            remaining: null,
            plan6w: remainingDays > 0 ? plannedPeople : null,
            needed: this.neededPeople(a, m),
            real4w: s.people_4w,
          },
          yield: {
            remaining: yieldBudgeted,
            plan6w: yieldPlanned,
            needed: remainingHours > 0 && pending > 0 ? remainingHours / pending : null,
            real4w: m ? m.yield4w : null,
          },
        },
      }
    },

    // Semana (fecha de corte) · proyecto · por qué se sigue la actividad (motivos de la tabla 3)
    sheetSubtitle() {
      const a = this.selectedActivity
      if (!a) return ''
      const reasons = this.reasonRows.find(row => row.id === a.id)
      return this.$t('performance_dashboard_sheet_subtitle', {
        week: this.projectWeek ? this.projectWeek.number : '—',
        date: this.cutoffDate,
        project: this.project ? this.project.name : '',
        reasons: reasons && reasons.text ? reasons.text : '—',
      })
    },

    sheetKpis() {
      const a = this.selectedActivity
      const d = this.sheet
      if (!a || !d) return []
      const notCalculable = this.$t('performance_dashboard_not_calculable_short')
      const floatUnknown = a.float == null
      const { budgeted, planned, actual } = d.past.yield
      const reference = budgeted != null ? budgeted : planned
      const overBudget = actual != null && budgeted > 0 ? ((actual - budgeted) * 100) / budgeted : null
      const yieldHint = [this.$t('performance_dashboard_kpi_actual_yield_hint', {
        budgeted: d.notCalculable.budgetedYield ? notCalculable : this.fmt(budgeted, 1),
        planned: d.notCalculable.plannedYield ? notCalculable : this.fmt(planned, 1),
      })]
      if (overBudget !== null) {
        yieldHint.push(this.$t('performance_dashboard_sheet_kpi_yield_over', { percent: this.fmtSignedPercent(overBudget, 0) }))
      }
      return [
        {
          label: this.$t('real_progress'),
          info: 'sheet_actual_progress',
          value: this.fmtPercent(a.actual, 0),
          hint: this.$t('performance_dashboard_sheet_kpi_progress_hint', { budgeted: this.fmtPercent(a.baseline, 0) }),
        },
        {
          label: this.$t('performance_dashboard_kpi_project_delay_days'),
          info: 'sheet_project_delay',
          value: this.fmtSignedDays(a.projectDelay),
          negative: a.projectDelay > 0,
          notCalculable: a.grossDelay != null && floatUnknown,
          hint: this.$t('performance_dashboard_kpi_project_delay_days_hint', {
            late: this.fmtSignedDays(a.grossDelay),
            float: floatUnknown ? notCalculable : this.fmt(a.float, 0),
          }),
        },
        {
          label: this.$t('performance_dashboard_kpi_weeks_to_program_end'),
          info: 'sheet_weeks_to_end',
          value: this.fmt(a.weeksToEnd),
          negative: a.weeksToEnd < 0,
          hint: this.$t('performance_dashboard_kpi_weeks_to_program_end_hint', { date: this.fmtDate(a.endDate), pending: `${this.fmt(d.pending)} ${d.unit}`.trim() }),
        },
        {
          label: this.$t('performance_dashboard_kpi_actual_yield', { unit: d.unit }),
          info: 'sheet_actual_yield',
          value: this.fmt(actual, 1),
          negative: actual != null && reference != null && actual > reference,
          hint: yieldHint.join(' · '),
        },
      ]
    },

    pastRows() {
      const d = this.sheet
      if (!d) return []
      const { progress, staff, yield: yieldData } = d.past
      const nc = d.notCalculable
      const cell = (value, decimals, options = {}) => (options.notCalculable
        ? { notCalculable: true }
        : { text: options.percent ? this.fmtPercent(value, decimals) : this.fmt(value, decimals, options.maxDecimals), alert: !!options.alert })
      const yieldReference = yieldData.budgeted != null ? yieldData.budgeted : yieldData.planned
      return [
        {
          label: this.$t('performance_dashboard_row_progress_percent'),
          cells: [
            cell(progress.budgeted, 0, { percent: true }),
            cell(progress.planned, 0, { percent: true }),
            cell(progress.actual, 0, { percent: true, alert: progress.actual != null && progress.planned != null && progress.actual < progress.planned }),
          ],
        },
        {
          label: this.$t('performance_dashboard_row_staff'),
          cells: [
            cell(staff.budgeted, 1, { notCalculable: nc.budgetedStaff, maxDecimals: 3 }),
            cell(staff.planned, 1, { maxDecimals: 3 }),
            cell(staff.actual, 1, { maxDecimals: 3 }),
          ],
        },
        {
          label: this.$t('performance_dashboard_row_yield', { unit: d.unit }),
          cells: [
            cell(yieldData.budgeted, 1, { notCalculable: nc.budgetedYield }),
            cell(yieldData.planned, 1, { notCalculable: nc.plannedYield }),
            cell(yieldData.actual, 1, { alert: yieldData.actual != null && yieldReference != null && yieldData.actual > yieldReference }),
          ],
        },
      ]
    },

    futureRows() {
      const d = this.sheet
      if (!d) return []
      const { rate, staff, yield: yieldData } = d.future
      const nc = d.notCalculable
      const cell = (value, decimals, options = {}) => (options.notCalculable
        ? { notCalculable: true }
        : { text: this.fmt(value, decimals), alert: !!options.alert })
      // En rojo el ritmo que no llega al necesario para terminar en fecha
      const belowNeeded = value => value != null && rate.needed != null && value < rate.needed
      return [
        {
          label: this.$t('performance_dashboard_row_progress_rate', { unit: d.unit }),
          cells: [
            cell(rate.remaining, 1),
            cell(rate.plan6w, 1, { alert: belowNeeded(rate.plan6w) }),
            cell(rate.needed, 1),
            cell(rate.real4w, 1, { alert: belowNeeded(rate.real4w) }),
          ],
        },
        {
          label: this.$t('performance_dashboard_row_staff'),
          cells: [
            cell(staff.remaining, 1, { notCalculable: nc.budgetedStaff }),
            cell(staff.plan6w, 1),
            cell(staff.needed, 1),
            cell(staff.real4w, 1, { notCalculable: nc.recentStaffYield }),
          ],
        },
        {
          label: this.$t('performance_dashboard_row_yield', { unit: d.unit }),
          cells: [
            cell(yieldData.remaining, 1, { notCalculable: nc.budgetedYield }),
            cell(yieldData.plan6w, 1, { notCalculable: nc.plannedYield }),
            cell(yieldData.needed, 1),
            cell(yieldData.real4w, 1, { notCalculable: nc.recentStaffYield }),
          ],
        },
      ]
    },

    sheetReviewRows() {
      const a = this.selectedActivity
      if (!a) return []
      const action = a.action
        ? [a.action, a.actionManager, a.actionDate ? this.fmtDate(a.actionDate) : null].filter(Boolean).join(' · ')
        : ''
      return [
        { label: this.$t('performance_dashboard_situation'), text: a.situation, bold: true, empty: this.$t('performance_dashboard_not_registered'), info: 'sheet_review' },
        { label: this.$t('performance_dashboard_col_cause_project'), text: a.cause, empty: this.$t('performance_dashboard_not_registered') },
        { label: this.$t('performance_dashboard_agreed_action'), text: action, empty: `— ${this.$t('performance_dashboard_no_action')} —` },
      ]
    },

    sheetNotCalculable() {
      const a = this.selectedActivity
      const d = this.sheet
      if (!a || !d) return []
      const items = []
      if (!a.boqCode) items.push(this.$t('performance_dashboard_nc_boq_item'))
      if (a.float == null) items.push(this.$t('performance_dashboard_nc_float'))
      if (d.notCalculable.budgetedStaff) items.push(this.$t('performance_dashboard_nc_budgeted_staff'))
      if (d.notCalculable.budgetedYield) items.push(this.$t(this.weightByHours ? 'performance_dashboard_nc_budgeted_yield_takeoff' : 'performance_dashboard_nc_budgeted_yield'))
      if (d.notCalculable.recentStaffYield) items.push(this.$t('performance_dashboard_nc_people_takeoff'))
      return items
    },

    // Últimas 6 semanas de la medición elegida: avance de cada semana (ud/semana), personas medias y h/ud.
    // Solo se dibujan las series con dato; las que el sistema no puede calcular se explican en chartsNotCalculable.
    weeklyCharts() {
      const a = this.selectedActivity
      const d = this.sheet
      if (!a || !d) return []
      const m = this.measurementOf(a)
      const takeoff = this.weightByHours
      const staffWeekly = a.sheetData.staff_weekly || []
      const yieldWeekly = m ? m.weeklyYield : []
      const series = m ? m.series : []
      const weekly = this.weeklyWindow.map((projectWeek) => {
        const number = projectWeek.number
        const quantity = series.find(week => week.number === number) || {}
        const staff = staffWeekly.find(week => week.number === number) || {}
        const yieldWeek = yieldWeekly.find(week => week.number === number) || {}
        return {
          label: `S${number}`,
          progress: { budgeted: this.valueOrNull(quantity.budgeted), planned: this.valueOrNull(quantity.plan), actual: this.valueOrNull(quantity.actual) },
          staff: { budgeted: this.valueOrNull(staff.budgeted), planned: this.valueOrNull(staff.planned), actual: this.valueOrNull(staff.actual) },
          // En takeoff el h/ud planificado es constante (índice HH); no hay h/ud semanal presupuestado ni real
          yield: takeoff
            ? { budgeted: null, planned: d.past.yield.planned, actual: null }
            : { budgeted: null, planned: this.valueOrNull(yieldWeek.planned), actual: this.valueOrNull(yieldWeek.actual) },
        }
      })
      const build = key => this.calculableSeries(weekly, key, [
        ['budgeted', this.$t('performance_dashboard_col_budgeted_short'), COLORS.budgeted],
        ['planned', this.$t('performance_dashboard_col_planned_short'), COLORS.planned],
        ['actual', this.$t('actual'), COLORS.actual],
      ])
      return [
        { title: this.$t('performance_dashboard_chart_weekly_progress'), ...build('progress'), info: 'weekly_progress' },
        { title: this.$t('performance_dashboard_chart_weekly_staff'), ...build('staff'), info: 'weekly_staff' },
        { title: this.$t('performance_dashboard_row_yield', { unit: d.unit }), ...build('yield'), info: 'weekly_yield' },
      ]
    },

    // Series que el sistema no puede dar en las gráficas de la ficha (no se dibujan)
    chartsNotCalculable() {
      const d = this.sheet
      if (!d) return []
      const items = []
      if (d.notCalculable.budgetedStaff && !this.weightByHours) items.push(this.$t('performance_dashboard_nc_budgeted_staff'))
      if (d.notCalculable.budgetedYield) items.push(this.$t(this.weightByHours ? 'performance_dashboard_nc_budgeted_yield_takeoff' : 'performance_dashboard_nc_budgeted_yield'))
      if (this.weightByHours) items.push(this.$t('performance_dashboard_nc_people_takeoff_charts'))
      return items
    },

    historyLibrary() {
      return {
        legend: { display: true, position: 'bottom', labels: { boxWidth: 10, fontSize: 10 } },
        scales: {
          xAxes: [{
            ticks: { fontSize: 9, maxRotation: 0, autoSkip: false },
            scaleLabel: { display: true, labelString: this.$t('performance_dashboard_col_work_month'), fontSize: 10 },
          }],
          yAxes: [{ ticks: { fontSize: 10 } }],
        },
      }
    },

    // Personas y h/ud necesarios: los valores constantes de «Necesario para llegar», solo en meses futuros
    historyCharts() {
      const a = this.selectedActivity
      const d = this.sheet
      if (!a || !d) return []
      const takeoff = this.weightByHours
      const neededStaff = d.future.staff.needed
      const neededYield = d.future.yield.needed
      const id = a.selectedMeasurementId
      const rows = (a.monthlyHistory || []).map((month) => {
        const bag = month.by_measurement || {}
        const figures = id != null ? (bag[id] || bag[String(id)] || {}) : {}
        const staff = month.staff || {}
        return {
          number: month.number,
          progress: month.progress,
          staff: {
            budgeted: this.valueOrNull(staff.budgeted),
            planned: this.valueOrNull(staff.planned),
            actual: this.valueOrNull(staff.actual),
            needed: month.future ? neededStaff : null,
          },
          yield: {
            // En takeoff el h/ud planificado es constante (índice HH); no hay h/ud mensual presupuestado ni real
            budgeted: null,
            planned: takeoff ? d.past.yield.planned : this.valueOrNull(figures.yield_planned),
            actual: this.valueOrNull(figures.yield),
            needed: month.future ? neededYield : null,
          },
        }
      })
      const series = (key, neededLabel) => this.calculableSeries(rows, key, [
        ['budgeted', this.$t('performance_dashboard_col_budgeted_short'), COLORS.budgeted, [6, 4]],
        ['planned', this.$t('performance_dashboard_col_planned_short'), COLORS.planned, []],
        ['actual', this.$t('actual'), COLORS.actual, []],
        ['needed', neededLabel, COLORS.remaining, [2, 3]],
      ], row => String(row.number))
      return [
        { title: this.$t('performance_dashboard_chart_cumulative_progress'), ...series('progress', this.$t('performance_dashboard_col_needed_short')), info: 'history_progress' },
        { title: this.$t('performance_dashboard_chart_monthly_staff'), ...series('staff', this.$t('performance_dashboard_col_needed_people_short')), info: 'history_staff' },
        { title: this.$t('performance_dashboard_row_yield', { unit: d.unit }), ...series('yield', this.$t('performance_dashboard_col_needed_short')), info: 'history_yield' },
      ]
    },

    // Últimos 12 meses de obra. Cantidad y rendimiento de la medición elegida.
    /**
     * Meses de obra desde el inicio de la actividad hasta el del corte (los futuros no tienen real); si son más de 12,
     * los últimos 12. Celdas: avance (ud) presup./planif./real, avance acum. presup./real, personas y h/ud
     * presup./planif./real y % horas perdidas. En rojo el real peor que el planificado; n/c lo que el sistema no puede dar.
     */
    monthlyEvolution() {
      const activity = this.selectedActivity
      const d = this.sheet
      if (!activity || !d || !activity.monthlyHistory) return []
      const past = activity.monthlyHistory.filter(month => !month.future)
      const history = past.length > 12 ? past.slice(past.length - 12) : past
      const nc = d.notCalculable
      const takeoff = this.weightByHours
      const measurementId = activity.selectedMeasurementId
      const value = v => (v === undefined ? null : v)
      const cell = (v, decimals, options = {}) => (options.notCalculable
        ? { notCalculable: true }
        : { text: options.percent ? this.fmtPercent(v, decimals) : this.fmt(v, decimals), alert: !!options.alert })
      return history.map((month) => {
        const bag = month.by_measurement || {}
        const figures = measurementId != null ? (bag[measurementId] || bag[String(measurementId)] || {}) : {}
        const staff = month.staff || {}
        const quantity = { budgeted: value(figures.budgeted), planned: value(figures.planned), actual: value(figures.actual) }
        const yieldMonth = {
          // En takeoff el h/ud planificado es constante (índice HH)
          budgeted: null,
          planned: takeoff ? d.past.yield.planned : value(figures.yield_planned),
          actual: value(figures.yield),
        }
        const yieldReference = yieldMonth.planned != null ? yieldMonth.planned : yieldMonth.budgeted
        return {
          number: month.number,
          cells: [
            cell(quantity.budgeted, 1),
            cell(quantity.planned, 1),
            cell(quantity.actual, 1, { alert: quantity.actual != null && quantity.planned != null && quantity.actual < quantity.planned }),
            cell(month.progress ? month.progress.budgeted : null, 0, { percent: true }),
            cell(month.progress ? month.progress.actual : null, 0, { percent: true }),
            cell(value(staff.budgeted), 1, { notCalculable: nc.budgetedStaff && !takeoff }),
            cell(value(staff.planned), 1),
            cell(value(staff.actual), 1, { notCalculable: takeoff }),
            cell(yieldMonth.budgeted, 1, { notCalculable: nc.budgetedYield }),
            cell(yieldMonth.planned, 1, { notCalculable: nc.plannedYield }),
            cell(yieldMonth.actual, 1, {
              notCalculable: takeoff,
              alert: yieldMonth.actual != null && yieldReference != null && yieldMonth.actual > yieldReference,
            }),
            cell(null, 0, { notCalculable: true }),
          ],
        }
      })
    },

    // Lo que no se puede calcular en las gráficas de la ficha y en la evolución mensual, con su razón
    monthlyNotCalculable() {
      return [...this.chartsNotCalculable, this.$t('performance_dashboard_nc_lost_hours')]
    },

    canEditReview() {
      return !!(this.project && this.project.can_admin === true)
    },
  },

  watch: {
    selectedActivityCode(code) {
      const activity = this.attentionActivities.find(item => item.code === code)
      if (activity) this.loadActivityCharts(activity.id)
    },
  },

  mounted() {
    if (this.project == null) {
      this.$router.push('/projects')
      return
    }

    this.$store.dispatch('app/updatePageInfo', {
      info: {
        title: this.project.name,
        subtitle: this.$t('performance_dashboard'),
        menuOption: '2-1',
        hidePageTitle: true,
      },
    })

    this.loadPerformanceActivities()
  },

  methods: {
    async loadPerformanceActivities() {
      if (!this.projectWeek) return

      this.loadingPerformance = true
      this.activityChartsLoaded = {}
      this.reviewDrafts = {}
      try {
        const { data } = await axios.get(`/api/v2/project/${this.project.id}/dashboard-performance/activities`, {
          params: this.periodParams(),
        })
        const payload = data && data.success ? data.data : null
        this.performanceActivities = payload ? payload.activities : []
        this.attentionMeasurementIds = this.defaultAttentionMeasurements(this.performanceActivities)
        if (payload && payload.windows) {
          this.performanceWindows = payload.windows
        }
        if (payload && payload.summary) {
          this.performanceSummary = Object.assign({}, this.performanceSummary, payload.summary)
        }
        if (payload && payload.thresholds) {
          this.performanceThresholds = payload.thresholds
        }
        this.weightBasis = payload && payload.weight_basis ? payload.weight_basis : 'cost'
        const first = this.attentionActivities[0]
        this.selectedActivityCode = first ? first.code : null
        // El watch de selectedActivityCode dispara loadActivityCharts
      } catch (error) {
        this.performanceActivities = []
        this.attentionMeasurementIds = {}
        this.activityChartsLoaded = {}
        this.selectedActivityCode = null
        this.$notify.error('error_loading_activities')
      } finally {
        this.loadingPerformance = false
      }
    },

    // PDF de la semana: el backend lo genera y devuelve una URL temporal; se abre para descargarlo
    async exportPdf() {
      if (this.exportingPdf || !this.projectWeek) return

      this.exportingPdf = true
      this.$notify.success('generating_report')
      try {
        const { data } = await axios.get(`/api/v2/project/${this.project.id}/dashboard-performance/export`, {
          params: this.periodParams(),
        })
        if (!data || !data.success || !data.url) {
          this.$notify.error('error_retrieving_data')
          return
        }
        window.open(data.url)
        this.$notify.success('report_generated')
      } catch (error) {
        this.$notify.error('error_retrieving_data')
      } finally {
        this.exportingPdf = false
      }
    },

    async loadActivityCharts(activityId) {
      if (!this.projectWeek || !activityId || this.activityChartsLoaded[activityId]) return

      this.loadingActivityCharts = true
      try {
        const { data } = await axios.get(
          `/api/v2/project/${this.project.id}/dashboard-performance/activities/${activityId}/charts`,
          { params: this.periodParams() },
        )
        const payload = data && data.success ? data.data : null
        if (!payload) return

        const chartsByMeasurement = {}
        ;(payload.measurements || []).forEach((measurement) => {
          chartsByMeasurement[measurement.measurement_id] = measurement
        })

        this.performanceActivities = this.performanceActivities.map((activity) => {
          if (activity.id !== activityId) return activity
          return {
            ...activity,
            monthly_history: payload.monthly_history || [],
            measurements: (activity.measurements || []).map((measurement) => {
              const charts = chartsByMeasurement[measurement.measurement_id]
              if (!charts) return measurement
              return {
                ...measurement,
                weekly_yield: charts.weekly_yield || [],
                series: charts.series || [],
              }
            }),
          }
        })
        this.activityChartsLoaded = { ...this.activityChartsLoaded, [activityId]: true }
      } catch (error) {
        this.$notify.error('error_loading_activities')
      } finally {
        this.loadingActivityCharts = false
      }
    },

    // Semana del proyecto y hora local: la línea base y la última planificación se calculan a este instante,
    // igual que en la planificación semanal
    periodParams() {
      return {
        week_start: this.projectWeek.start.format('YYYY-MM-DD'),
        week_end: this.projectWeek.end.format('YYYY-MM-DD'),
        now: moment().format('YYYY-MM-DD HH:mm:ss'),
      }
    },

    performanceSeriesOf(getValue) {
      return this.orderedPerformanceActivities.map(activity => [activity.code || activity.name, getValue(activity)])
    },

    // La primaria queda elegida; si no hay primaria el select empieza vacío.
    defaultAttentionMeasurements(activities) {
      const selected = {}
      activities.forEach((activity) => {
        const measurements = activity.measurements || []
        const primary = measurements.find((measurement) => measurement.primary)
        if (primary) {
          selected[activity.id] = primary.measurement_id
        }
      })
      return selected
    },

    setAttentionMeasurement(activityId, measurementId) {
      const next = { ...this.attentionMeasurementIds }
      if (measurementId === '' || measurementId == null) {
        delete next[activityId]
      } else {
        next[activityId] = Number(measurementId)
      }
      this.attentionMeasurementIds = next
    },


    /**
     * Series de una gráfica de la ficha con solo las que tienen algún valor: una serie que el sistema no puede
     * calcular no se dibuja ni sale en la leyenda. Devuelve { data, colors } para chartkick.
     * Cada serie: [clave, nombre, color, trazo]; con trazo es una línea (histórico mensual: continuo, raya o punto).
     */
    calculableSeries(rows, key, series, labelOf = row => row.label) {
      const hasValue = value => value !== null && value !== undefined
      const drawn = series
        .map(([field, name, color, dash]) => ({
          color,
          item: {
            name,
            data: rows.map(row => [labelOf(row), row[key][field]]),
            ...(dash ? { dataset: { fill: false, lineTension: 0, pointRadius: 3, borderDash: dash, spanGaps: false } } : {}),
          },
        }))
        .filter(({ item }) => item.data.some(([, value]) => hasValue(value)))
      return { data: drawn.map(({ item }) => item), colors: drawn.map(({ color }) => color) }
    },

    measurementOf(activity) {
      if (!activity || !activity.measurements.length) return null
      return activity.measurements.find(measurement => measurement.id === activity.selectedMeasurementId) || null
    },

    // Personas necesarias = pendiente × h/ud real reciente (4 semanas; si no hay, a origen) / (días laborables que quedan × jornada)
    neededPeople(activity, measurement) {
      if (!activity || !measurement) return null
      const sheet = activity.sheetData || {}
      const days = sheet.remaining_days || 0
      const manHour = sheet.man_hour || this.projectManHour
      const yieldReal = measurement.yield4w > 0 ? measurement.yield4w : measurement.yieldActual
      if (!(measurement.pending > 0) || !(yieldReal > 0) || days <= 0) return null
      return (measurement.pending * yieldReal) / (days * manHour)
    },

    valueOrNull(value) {
      return value === undefined ? null : value
    },

    selectActivity(code) {
      this.selectedActivityCode = code
    },

    reviewFrom(activity) {
      if (!activity) return emptyReview()
      return {
        priority: activity.priority || '',
        situation: activity.situation || '',
        cause: activity.cause || '',
        action: activity.action || '',
        manager: activity.actionManager || '',
        date: activity.actionDate || null,
        status: activity.actionStatus || 'pending',
      }
    },

    draftOf(activity) {
      return this.reviewDrafts[activity.id] || this.reviewFrom(activity)
    },

    setReviewDraft(activity, field, value, save = false) {
      this.reviewDrafts = { ...this.reviewDrafts, [activity.id]: { ...this.draftOf(activity), [field]: value } }
      if (save) this.saveReview(activity)
    },

    isReviewDirty(activity) {
      return ['priority', 'situation', 'cause', 'action', 'manager', 'date'].some(field => this.isReviewFieldDirty(activity, field))
    },

    isReviewFieldDirty(activity, field) {
      const draft = this.reviewDrafts[activity.id]
      if (!draft) return false
      return (draft[field] || '') !== (this.reviewFrom(activity)[field] || '')
    },

    // Borde naranja mientras el cambio no se ha guardado
    reviewInputClasses(activity, field) {
      return [REVIEW_INPUT_CLASSES, this.isReviewFieldDirty(activity, field) ? 'border-orange bg-yellow-lightest' : 'border-grey bg-white']
    },

    // Se guarda la fila entera. Si esa fila ya se está guardando, se repite al terminar con el último borrador.
    async saveReview(activity) {
      if (!this.canEditReview || !this.isReviewDirty(activity)) return
      const activityId = activity.id
      if (this.savingReviewIds[activityId]) {
        this.savingReviewIds = { ...this.savingReviewIds, [activityId]: 'queued' }
        return
      }
      const draft = this.reviewDrafts[activityId]
      this.savingReviewIds = { ...this.savingReviewIds, [activityId]: true }
      try {
        const { data } = await axios.put(
          `/api/v2/project/${this.project.id}/dashboard-performance/activities/${activityId}/review`,
          {
            priority: draft.priority || null,
            situation: draft.situation || null,
            cause: draft.cause || null,
            action: draft.action || null,
            action_manager: draft.manager || null,
            action_date: draft.date || null,
            action_status: draft.action ? (activity.actionStatus || 'pending') : null,
          }
        )
        if (!data || !data.success) {
          this.$notify.error('save_error')
          return
        }
        this.performanceActivities = this.performanceActivities.map((item) => {
          if (item.id !== activityId) return item
          return Object.assign({}, item, { review: data.review || null })
        })
        if (this.reviewDrafts[activityId] === draft) {
          const drafts = { ...this.reviewDrafts }
          delete drafts[activityId]
          this.reviewDrafts = drafts
        }
        this.$notify.success('performance_dashboard_action_saved')
      } catch (error) {
        this.$notify.error('save_error')
      } finally {
        const queued = this.savingReviewIds[activityId] === 'queued'
        const saving = { ...this.savingReviewIds }
        delete saving[activityId]
        this.savingReviewIds = saving
        if (queued) {
          const fresh = this.attentionActivities.find(item => item.id === activityId)
          if (fresh) this.saveReview(fresh)
        }
      }
    },

    criticalityOf(activity) {
      if (activity.critical) return 'yes'
      if (activity.float != null && activity.grossDelay != null && activity.grossDelay > activity.float) return 'becomes_yes'
      return 'no'
    },

    openInfo(topic) {
      this.infoTopic = topic
    },

    // '—' para valores sin dato
    // maxDecimals > decimals: muestra hasta maxDecimals sin rellenar con ceros
    fmt(value, decimals = 1, maxDecimals = decimals) {
      if (value === null || value === undefined || Number.isNaN(value)) return '—'
      return new Intl.NumberFormat(this.$i18n.locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: Math.max(decimals, maxDecimals),
      }).format(value)
    },

    fmtPercent(value, decimals = 0) {
      const formatted = this.fmt(value, decimals)
      return formatted === '—' ? formatted : formatted + '%'
    },

    fmtSignedDays(value) {
      const formatted = this.fmt(value, 0)
      return value > 0 ? '+' + formatted : formatted
    },

    fmtSignedPercent(value, decimals = 1) {
      const formatted = this.fmtPercent(value, decimals)
      return value > 0 ? '+' + formatted : formatted
    },

    fmtDate(date) {
      return date ? moment(date).format('DD/MM/YYYY') : '—'
    },
  },
}
</script>
